import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Search } from 'lucide-react';
import { FleetBadge } from './FleetTicks';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const saveFleetClass = async (headers, registrations, fleet_class) => {
  const res = await axios.put(`${API_URL}/api/fuel-tracker/fleet-classes`, { registrations, fleet_class }, { headers });
  toast.success(`${registrations.length} vehicle(s) set to ${fleet_class} · ${res.data.entries_relabelled} fuel entries relabelled`);
  return res.data;
};

export const FleetClassSelect = ({ value, onChange, testId }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className={`rounded border px-2 py-1 text-xs font-semibold ${value === 'internal' ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-indigo-50 border-indigo-300 text-indigo-800'}`}
    data-testid={testId}
  >
    <option value="internal">Internal</option>
    <option value="external">External</option>
  </select>
);

const BulkBar = ({ count, onSet }) => (
  <div className="flex items-center gap-2 text-sm" data-testid="fleet-class-bulk-bar">
    <span className="text-slate-600">{count} ticked</span>
    <Button size="sm" disabled={!count} className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onSet('internal')} data-testid="fleet-class-bulk-internal">Set Internal</Button>
    <Button size="sm" disabled={!count} className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => onSet('external')} data-testid="fleet-class-bulk-external">Set External</Button>
  </div>
);

export const FleetClassPanel = ({ headers, onChanged }) => {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [ticked, setTicked] = useState({});

  const load = useCallback(async () => {
    try { setRows((await axios.get(`${API_URL}/api/fuel-tracker/fleet-classes`, { headers })).data); }
    catch { toast.error('Could not load fleet classes'); }
  }, [headers]);
  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => rows.filter(r => r.registration.includes(q.trim().toUpperCase())), [rows, q]);
  const tickedRegs = Object.keys(ticked).filter(k => ticked[k]);
  const allTicked = shown.length > 0 && shown.every(r => ticked[r.registration]);

  const apply = async (regs, fc) => {
    try { await saveFleetClass(headers, regs, fc); setTicked({}); await load(); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const toggleAll = () => setTicked(allTicked ? {} : Object.fromEntries(shown.map(r => [r.registration, true])));

  return (
    <Card className="bg-white border-slate-200" data-testid="fleet-class-panel">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
        <CardTitle className="text-slate-900">Fleet Classification — Internal / External</CardTitle>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2 top-2.5 text-slate-400" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search registration" className="pl-8 h-9 w-48" data-testid="fleet-class-search" />
          </div>
          <BulkBar count={tickedRegs.length} onSet={(fc) => apply(tickedRegs, fc)} />
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-slate-500 mb-3">Changing a vehicle relabels all its past fuel entries and applies to all future entries.</p>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10"><input type="checkbox" checked={allTicked} onChange={toggleAll} data-testid="fleet-class-tick-all" /></TableHead>
              <TableHead>Registration</TableHead>
              <TableHead className="text-right">Fuel entries</TableHead>
              <TableHead>Current</TableHead>
              <TableHead>Set to</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.map(r => (
              <TableRow key={r.registration} data-testid={`fleet-class-row-${r.registration}`}>
                <TableCell><input type="checkbox" checked={!!ticked[r.registration]} onChange={() => setTicked(t => ({ ...t, [r.registration]: !t[r.registration] }))} data-testid={`fleet-class-tick-${r.registration}`} /></TableCell>
                <TableCell className="font-mono font-semibold">{r.registration}{r.manual && <span className="ml-2 text-[10px] text-slate-400 uppercase">manual</span>}</TableCell>
                <TableCell className="text-right font-mono">{r.entries}</TableCell>
                <TableCell><FleetBadge fc={r.fleet_class} testId={`fleet-class-badge-${r.registration}`} /></TableCell>
                <TableCell><FleetClassSelect value={r.fleet_class} onChange={(fc) => apply([r.registration], fc)} testId={`fleet-class-select-${r.registration}`} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {shown.length === 0 && <p className="text-center text-sm text-slate-500 py-6">No vehicles found.</p>}
      </CardContent>
    </Card>
  );
};
